import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, ArrowLeft, RefreshCw, Loader2, Save, RotateCcw,
  ChevronRight, Edit3, Check, X, Globe2, Scale, Shield, Cookie, Receipt, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_ICONS = {
  terms: Scale,
  privacy: Shield,
  cookies: Cookie,
  refund: Receipt,
  imprint: Building2
};

// Page card component
const PageCard = ({ page, onClick, isSelected }) => {
  const Icon = PAGE_ICONS[page.page_id] || FileText;
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        isSelected 
          ? 'border-primary bg-primary/5 shadow-sm' 
          : 'border-stone-200 hover:border-primary/50 hover:bg-stone-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-stone-400'}`} />
          <div>
            <p className="font-medium text-sm">{page.page_name.split(' / ')[0]}</p>
            <p className="text-xs text-muted-foreground">{page.section_count} osiota</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {page.has_custom_content && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
              Muokattu
            </span>
          )}
          <ChevronRight className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-stone-300'}`} />
        </div>
      </div>
    </button>
  );
};

// Section editor component
const SectionEditor = ({ section, onChange, language }) => {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: language === 'fi' ? section.title_fi : section.title_en,
    content: language === 'fi' ? section.content_fi : section.content_en
  });

  useEffect(() => {
    setEditData({
      title: language === 'fi' ? section.title_fi : section.title_en,
      content: language === 'fi' ? section.content_fi : section.content_en
    });
  }, [section, language]);

  const handleSave = () => {
    const updatedSection = { ...section };
    if (language === 'fi') {
      updatedSection.title_fi = editData.title;
      updatedSection.content_fi = editData.content;
    } else {
      updatedSection.title_en = editData.title;
      updatedSection.content_en = editData.content;
    }
    onChange(updatedSection);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      title: language === 'fi' ? section.title_fi : section.title_en,
      content: language === 'fi' ? section.content_fi : section.content_en
    });
    setEditing(false);
  };

  return (
    <div className="p-5 rounded-xl border border-stone-200 hover:border-stone-300 transition-all">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <p className="text-xs text-muted-foreground font-mono mb-1">
            {section.section_id}
          </p>
          {editing ? (
            <Input
              value={editData.title}
              onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
              className="font-semibold"
              placeholder="Otsikko"
            />
          ) : (
            <h3 className="font-semibold">{language === 'fi' ? section.title_fi : section.title_en}</h3>
          )}
        </div>
        {!editing ? (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={handleSave}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel}>
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        )}
      </div>
      
      {editing ? (
        <Textarea
          value={editData.content}
          onChange={(e) => setEditData(prev => ({ ...prev, content: e.target.value }))}
          className="min-h-[200px] text-sm"
          placeholder="Sisältö..."
        />
      ) : (
        <p className="text-sm text-muted-foreground whitespace-pre-line">
          {(language === 'fi' ? section.content_fi : section.content_en) || 
           <span className="italic">Ei sisältöä</span>}
        </p>
      )}
    </div>
  );
};

export default function AdminLegalPages() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('fi');
  const [hasChanges, setHasChanges] = useState(false);

  // Load pages list
  const loadPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/legal/pages`, {
        withCredentials: true
      });
      setPages(response.data.pages);
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load pages:', error);
        toast.error('Sivujen lataus epäonnistui');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load page content
  const loadPageContent = useCallback(async (pageId) => {
    try {
      const response = await axios.get(`${API}/admin/legal/pages/${pageId}`, {
        withCredentials: true
      });
      setPageContent(response.data);
      setHasChanges(false);
    } catch (error) {
      logger.error('Failed to load page content:', error);
      toast.error('Sisällön lataus epäonnistui');
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  useEffect(() => {
    if (selectedPage) {
      loadPageContent(selectedPage);
    }
  }, [selectedPage, loadPageContent]);

  // Handle section change
  const handleSectionChange = (updatedSection) => {
    setPageContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.section_id === updatedSection.section_id ? updatedSection : s
      )
    }));
    setHasChanges(true);
  };

  // Save changes
  const handleSave = async () => {
    if (!pageContent) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/legal/pages/${selectedPage}`, {
        page_id: selectedPage,
        sections: pageContent.sections
      }, { withCredentials: true });
      
      toast.success('Muutokset tallennettu');
      setHasChanges(false);
      loadPages(); // Refresh list to show "Muokattu" badge
    } catch (error) {
      logger.error('Failed to save:', error);
      toast.error('Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  // Reset to default
  const handleReset = async () => {
    if (!window.confirm('Haluatko varmasti palauttaa tämän sivun alkuperäiseen sisältöön? Kaikki muokkauksesi menetetään.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/legal/pages/${selectedPage}/reset`, {
        withCredentials: true
      });
      toast.success('Sivu palautettu alkuperäiseen');
      loadPageContent(selectedPage);
      loadPages();
    } catch (error) {
      logger.error('Failed to reset:', error);
      toast.error('Palautus epäonnistui');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <FileText className="h-16 w-16 text-stone-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Pääsy estetty</h1>
          <p className="text-muted-foreground mb-6">
            Vain ylläpitäjät voivat muokata legal-sivuja.
          </p>
          <Button onClick={() => navigate('/')}>
            Takaisin etusivulle
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin/analytics')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Takaisin
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6 text-primary" />
                Legal-sivujen Hallinta
              </h1>
              <p className="text-muted-foreground">
                Muokkaa käyttöehtoja, tietosuojakäytäntöä ja muita legal-sivuja
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="flex rounded-lg border border-stone-200 overflow-hidden">
              <button
                onClick={() => setSelectedLanguage('fi')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  selectedLanguage === 'fi' 
                    ? 'bg-primary text-white' 
                    : 'bg-white hover:bg-stone-50'
                }`}
              >
                <span className="text-lg">🇫🇮</span> Suomi
              </button>
              <button
                onClick={() => setSelectedLanguage('en')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  selectedLanguage === 'en' 
                    ? 'bg-primary text-white' 
                    : 'bg-white hover:bg-stone-50'
                }`}
              >
                <span className="text-lg">🇬🇧</span> English
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Pages sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-stone-200 p-4">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Globe2 className="h-4 w-4" />
                Sivut
              </h2>
              <div className="space-y-2">
                {pages.map(page => (
                  <PageCard
                    key={page.page_id}
                    page={page}
                    isSelected={selectedPage === page.page_id}
                    onClick={() => setSelectedPage(page.page_id)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Content editor */}
          <div className="lg:col-span-3">
            {pageContent ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">{pageContent.page_name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {pageContent.sections.length} osiota
                      {hasChanges && (
                        <span className="text-amber-600 ml-2">
                          (tallentamattomia muutoksia)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {pageContent.is_custom && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Palauta
                      </Button>
                    )}
                    <Button
                      onClick={handleSave}
                      disabled={!hasChanges || saving}
                      className="gap-2"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Tallenna
                    </Button>
                  </div>
                </div>

                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {pageContent.sections.map((section) => (
                    <SectionEditor
                      key={section.section_id}
                      section={section}
                      language={selectedLanguage}
                      onChange={handleSectionChange}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <FileText className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Valitse sivu</h2>
                <p className="text-muted-foreground">
                  Valitse vasemmalta sivu, jonka sisältöä haluat muokata
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <h3 className="font-semibold text-blue-900 mb-2">Ohje</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Valitse ensin kieli (Suomi tai English) ylhäältä</li>
            <li>• Valitse sivu vasemmalta (Terms, Privacy, jne.)</li>
            <li>• Klikkaa kynäikonia muokataksesi osiota</li>
            <li>• Voit käyttää rivinvaihtoja sisällössä</li>
            <li>• Muista tallentaa muutokset &quot;Tallenna&quot; -napista</li>
            <li>• &quot;Palauta&quot; palauttaa sivun alkuperäiseen sisältöön</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
